import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { Table, Button, Select, Space, Modal, Form, Input, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../../shared/styles/ErpPages.css";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";

/**
 * ERP vendors index — same shell as customers: View / Edit / soft-Delete.
 */
const Vendors = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("vendors.index");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("debt");
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const mainCode = systemConfig?.currency_code || "IQD";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const res = await apiClient.get("/vendors");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || t("vendorsLoadFailed"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const withDebt = rows.filter((r) => toApiNumber(r.balance_main, 0) > 0.0001);
    return {
      all: rows.length,
      debt: withDebt.length,
      clear: rows.length - withDebt.length,
      debtTotal: withDebt.reduce((s, r) => s + toApiNumber(r.balance_main, 0), 0),
    };
  }, [rows]);

  const list = useMemo(() => {
    const scoped = rows.filter((r) => {
      const bal = toApiNumber(r.balance_main, 0);
      if (filter === "debt" && bal <= 0.0001) return false;
      if (filter === "clear" && bal > 0.0001) return false;
      return true;
    });
    return filterBySearch(scoped, search, (r) => [
      r.id,
      r.name,
      r.phone,
      r.email,
      r.notes,
      r.balance_main,
    ]);
  }, [rows, search, filter]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone || "",
      email: record.email || "",
      notes: record.notes || "",
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await apiClient.put(`/vendors/${editing.id}`, values);
        message.success(t("vendorsUpdated"));
      } else {
        await apiClient.post("/vendors", values);
        message.success(t("vendorsCreated"));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("vendorsSaveFailed"));
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t("vendorsDeleteConfirm", "Archive this vendor?"),
      content: t(
        "vendorsSoftDeleteHint",
        "This is a soft delete. Purchase order history stays available."
      ),
      okType: "danger",
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await apiClient.delete(`/vendors/${record.id}`);
          message.success(t("vendorsDeleted", "Vendor archived."));
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("vendorsDeleteFailed"));
        }
      },
    });
  };

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("purchasing", "Purchasing")} / {t("vendorsTitle", "Vendors")}
            </div>
            <h1>{t("vendorsTitle", "Vendors")}</h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            {canManage && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t("vendorsAdd", "New vendor")}
              </Button>
            )}
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{counts.all}</b>
          </span>
          <span>
            {t("withBalance", "With balance")}: <b>{counts.debt}</b>
          </span>
          <span>
            {t("openPayables", "Open payables")}:{" "}
            <b className="erp-money debt">{formatCurrency(counts.debtTotal, mainCode)}</b>
          </span>
        </div>

        <div className="erp-toolbar">
          <Select
            value={filter}
            onChange={setFilter}
            style={{ width: 200 }}
            options={[
              { value: "debt", label: `${t("withBalance", "With balance")} (${counts.debt})` },
              { value: "clear", label: `${t("zeroBalance", "Zero balance")} (${counts.clear})` },
              { value: "all", label: `${t("all", "All")} (${counts.all})` },
            ]}
          />
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder={t("searchVendorPlaceholder", "Code / name / phone")}
            width={280}
          />
        </div>

        <div className="erp-body">
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={list}
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `${total} ${t("records", "records")}`,
            }}
            locale={{ emptyText: t("noDataFound", "No records") }}
            columns={[
              {
                title: t("id", "ID"),
                dataIndex: "id",
                width: 70,
                sorter: (a, b) => a.id - b.id,
              },
              {
                title: t("vendorsColName", "Name"),
                dataIndex: "name",
                sorter: (a, b) => String(a.name || "").localeCompare(String(b.name || "")),
                render: (v, r) => (
                  <Button
                    type="link"
                    style={{ padding: 0, height: "auto", fontWeight: 600 }}
                    onClick={() => navigate(`/vendors/${r.id}`)}
                  >
                    {v || `Vendor #${r.id}`}
                  </Button>
                ),
              },
              {
                title: t("vendorsColPhone", "Phone"),
                dataIndex: "phone",
                width: 140,
                render: (v) => v || "—",
              },
              {
                title: t("vendorsColEmail", "Email"),
                dataIndex: "email",
                width: 180,
                render: (v) => v || "—",
              },
              {
                title: t("vendorsColBalance", "Balance"),
                dataIndex: "balance_main",
                width: 150,
                align: "right",
                sorter: (a, b) => toApiNumber(a.balance_main, 0) - toApiNumber(b.balance_main, 0),
                defaultSortOrder: "descend",
                render: (v) => {
                  const owed = toApiNumber(v, 0);
                  return owed > 0.0001 ? (
                    <span className="erp-money debt">{formatCurrency(v, mainCode)}</span>
                  ) : (
                    <span className="erp-money ok">0</span>
                  );
                },
              },
              {
                title: t("status", "Status"),
                key: "status",
                width: 110,
                render: (_, r) =>
                  toApiNumber(r.balance_main, 0) > 0.0001 ? (
                    <span className="erp-status debt">{t("open", "Open")}</span>
                  ) : (
                    <span className="erp-status clear">{t("settled", "Settled")}</span>
                  ),
              },
              {
                title: t("action", "Action"),
                key: "action",
                width: 220,
                fixed: "right",
                render: (_, r) => (
                  <Space size={4} wrap>
                    <Button
                      size="small"
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => navigate(`/vendors/${r.id}`)}
                    >
                      {t("view", "View")}
                    </Button>
                    {canManage && (
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
                        {t("edit", "Edit")}
                      </Button>
                    )}
                    {canManage && (
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(r)}
                      >
                        {t("delete", "Delete")}
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title={editing ? t("vendorsEditTitle") : t("vendorsCreateTitle")}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={t("save", "Save")}
        cancelText={t("cancel", "Cancel")}
        width={480}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t("purchaseOrdersVendorName")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t("purchaseOrdersVendorPhone")}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t("purchaseOrdersVendorEmail")}>
            <Input type="email" />
          </Form.Item>
          <Form.Item name="notes" label={t("vendorsNotes")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Vendors;
